/**
 * Weekly-rotating "Try asking" starters for the Oracle page.
 *
 * A curated pool of correlation-flavoured prompts, rotated in stable rounds
 * keyed on the current ISO week. Everyone globally sees the same 4 prompts
 * during the same week — the rotation feels coordinated ("this is what the
 * community is exploring this week") rather than random per-visit.
 *
 * Rotation math is deterministic + pure client-side (no backend call), so it
 * costs nothing and works offline. Once the pool has been fully cycled, it
 * loops back to the start.
 */

// The pool. Add to this list to introduce new prompts to the rotation. Keep
// each one under ~90 chars so it renders in one line on mobile.
export const ORACLE_STARTER_POOL = [
  "What connects Logos, Aum, and the Tao?",
  "How is Kundalini related to the DNA helix?",
  "Is the Merkabah in Ezekiel related to modern UAP reports?",
  "What do Nigredo and the Dark Night of the Soul share?",
  "Compare Anatta (Buddhism) with the death of the ego in Jung.",
  "How is the Christian Trinity mirrored in Vedanta's Sat-Chit-Ananda?",
  "What is the resonance between Sufi fana and Zen mushin?",
  "How does the Book of Enoch echo Sumerian Anunnaki accounts?",
  "Is the World Tree in Kabbalah the same as Yggdrasil?",
  "What do Plato's Forms and Jung's archetypes share?",
  "Trace the shape of enlightenment across Zen, Vedanta, and Stoicism.",
  "How does the Hero's Journey map to alchemical transmutation?",
  "What is the physical analog of the Akashic Records?",
  "Are quantum entanglement and Indra's Net describing the same thing?",
  "How does the Egyptian Ka relate to the etheric body in Theosophy?",
  "What do Ma'at, dharma, and the Tao have in common?",
  "Compare the fall of Lucifer with Prometheus stealing fire.",
  "How is the Kabbalistic Ain Soph reflected in Buddhist Śūnyatā?",
  "What is the correlation between the chakras and the endocrine system?",
  "How do Gnostic archons compare to Buddhist mara?",
  "Are the Anunnaki, the Elohim, and the Watchers the same beings?",
  "What connects Hermeticism's 'As above, so below' to fractal geometry?",
  "How does the Bhagavad Gita's battlefield map onto inner conflict?",
  "Compare Christ's 40 days in the desert with Siddhartha under the Bodhi tree.",
  "What is the shared substrate beneath prayer, mantra, and affirmation?",
  "How does the Fibonacci sequence show up in sacred architecture?",
  "What do the Law of One and the Bodhisattva vow both point at?",
  "Trace 'the Word' from John 1 through Sanskrit Vak to modern information theory.",
  "How is the Sufi 'polishing the heart' the same as Stoic prosoche?",
  "What is the modern-physics analog of Brahman?",
  "Compare Rumi's Beloved with the Christian Bride of Christ.",
  "How does the Egyptian weighing of the heart map to Buddhist karma?",
  "What connects the Kaaba, the axis mundi, and the Tree of Life?",
];

/**
 * ISO-week number for a given date. Same value for everyone on Earth in a
 * given calendar week — so all users see the same rotation window.
 * Cheap, dep-free implementation.
 */
function isoWeekNumber(d = new Date()) {
  // Set to nearest Thursday: current date + 4 - current day number.
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNr = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNr + 3);
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  const firstDayNr = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNr + 3);
  const weekNum = 1 + Math.round((target - firstThursday) / (7 * 24 * 3600 * 1000));
  // Fold year in so week-1-of-2027 ≠ week-1-of-2026 in the rotation.
  return target.getUTCFullYear() * 100 + weekNum;
}

/**
 * Return `count` prompts for the current week. Cycles through the pool in
 * chunks of `count`, so week 1 → items 0–3, week 2 → items 4–7, etc.
 * Wraps at the end (with a shift on the second lap so the same block never
 * comes back consecutively).
 */
export function weeklyOracleStarters(count = 4, now = new Date()) {
  const pool = ORACLE_STARTER_POOL;
  const N = pool.length;
  if (N === 0) return [];
  const wk = isoWeekNumber(now);
  const start = (wk * count) % N;
  const out = [];
  for (let i = 0; i < count; i++) {
    out.push(pool[(start + i) % N]);
  }
  return out;
}
